import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useModalFocus } from "../lib/modal-focus.ts";
import { useScreen } from "../lib/layout.ts";
import { cn } from "../lib/utils.ts";
import {
  AtSign,
  Bell,
  BellRing,
  CalendarDays,
  Gauge,
  Megaphone,
  PackageCheck,
  PencilLine,
  X,
} from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  COLLAB_TEXTS,
  RELEASE_NOTE_TEXTS,
  RELEASE_NOTES_PATH,
} from "@till-erp/collab/values";
import {
  apiFetch,
  ApiProblem,
  newIdempotencyKey,
  type ActivityItem,
  type NotificationItem,
  type NotificationList,
  type ReleaseNote,
} from "../lib/api.ts";
import { entityPath } from "../lib/entities.ts";
import {
  formatDate,
  formatWhen,
  isoDateInBerlin,
  previousIsoDateInBerlin,
} from "../lib/format.ts";
import { useBusinessNow } from "../lib/business-time.ts";
import { RELEASE_NOTES_KEY, releaseNoteLink } from "../lib/release-notes.ts";
import { useSession } from "../lib/session.tsx";

const NOTIFICATIONS_KEY = ["notifications"] as const;
/** Abfrage „Letzte Ereignisse“ (früher Karte der Startseite, Story 1.79; seit Ticket 1.243 Reiter der Glocke). */
const ACTIVITY_KEY = ["activity"] as const;
const ACTIVITY_LIMIT = 20;

/** Texte der Glocke (Ticket 1.243, Entwurf A05). */
const BELL_TEXTS = {
  title: "Benachrichtigungen",
  tabs: "Benachrichtigungen filtern",
  all: "Alle",
  unread: "Ungelesen",
  mentions: "Erwähnungen",
  events: "Ereignisse",
  /** Zugangsname des Reiters „Ereignisse“ (enthält den sichtbaren Text). */
  eventsLong: "Letzte Ereignisse",
  markAll: "Alle gelesen",
  close: "Benachrichtigungen schließen",
  back: "Zurück",
  today: "Heute",
  yesterday: "Gestern",
  older: "Älter",
  unreadEmpty: "Keine ungelesenen Benachrichtigungen.",
  mentionsEmpty:
    "Keine Erwähnungen. Hier erscheint, wenn dich jemand mit @ in einer Notiz erwähnt.",
  eventsEmpty: "Noch keine Ereignisse.",
  protocol: "Ganzes Protokoll →",
  settings: "Einstellungen › Benachrichtigungen",
  /** Entwurfsteil ohne Funktion (Rudolf 17.10.2026): ausgegraut mit Hinweis. */
  later: "kommt mit einer späteren Story",
  /** Benachrichtigungs-Einstellungen: Einstellungs-Seiten nach Entwurf (Story 1.252). */
  laterSettings: "kommt mit Story 1.252",
  count: (all: number, unread: number) =>
    `${all} ${all === 1 ? "Benachrichtigung" : "Benachrichtigungen"} · ${unread} ungelesen`,
} as const;

type BellTab = "all" | "unread" | "mentions" | "events";
const TAB_ORDER: readonly BellTab[] = ["all", "unread", "mentions", "events"];
/** Rolle Mitarbeiter (Story 22.11): nur persönliche Meldungen – keine Erwähnungen, keine Ereignisse im Team. */
const STAFF_TAB_ORDER: readonly BellTab[] = ["all", "unread"];

/** Glocke lesen: alle 60 s und bei Fokus (wie der Zähler „Aufgaben“); keine Einblendung. */
function useNotifications() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () =>
      apiFetch<NotificationList>(getToken, "/api/v1/notifications"),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

function NotificationEntry({
  item,
  unread,
  onOpen,
}: {
  item: NotificationItem;
  unread: boolean;
  onOpen: () => void;
}) {
  // Systemhinweis (Story 2.9): führt an seinen eigenen Ort (z. B. Einstellungen › E-Mail), nicht in einen Chatter.
  const system = item.kind === "system";
  // Beobachtete Ansicht (Story 1.34): führt zur Liste mit den neuen Zeilen markiert; Satz und Ort stehen im Eintrag.
  const watch = item.kind === "watch";
  // Bereichs-Hinweis (Story 7.17): führt zu seiner eigenen Seite (z. B. Meldung zum Wareneingang).
  const area = item.kind === "area";
  // Persönliche Meldung (Story 22.6, z. B. Urlaubsantrag oder Entscheidung): führt an ihren Ort (Genehmigen, Urlaub).
  const person = item.kind === "person";
  const ownPlace = system || watch || area || person;
  const path = item.canOpen
    ? (item.href ?? entityPath(item.entityType, item.entityId))
    : null;
  // Mit Knopftext (z. B. „Jetzt gehen“): Meldung selbst ohne Link, darunter ein Textlink zum Ort (kein Link im Link).
  const linkLabel = path && item.linkLabel ? item.linkLabel : null;
  const Icon = system
    ? Gauge
    : watch
      ? BellRing
      : area
        ? PackageCheck
        : person
          ? CalendarDays
          : item.kind === "mention"
            ? AtSign
            : PencilLine;
  const content = (
    <>
      <span
        aria-hidden
        className="grid size-icon-circle flex-none place-items-center rounded-full bg-fill text-ink-2"
      >
        <Icon size={16} strokeWidth={1.5} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-row font-semibold text-ink [overflow-wrap:anywhere]">
          {unread ? <span className="sr-only">Ungelesen: </span> : null}
          {item.text}
        </span>
        <span className="type-meta block text-ink-3">
          {/* Erwähnung ohne Leserecht (Bug 28): nur die Art des Datensatzes, kein Name. */}
          {ownPlace || (item.kind === "mention" && !item.canOpen)
            ? item.entityLabel
            : `${item.entityLabel} ${item.entityName}`}{" "}
          · {formatWhen(item.at)}
        </span>
        {item.body ? (
          <span className="mt-1 block text-row whitespace-pre-wrap text-ink-2 [overflow-wrap:anywhere]">
            {item.body}
          </span>
        ) : null}
        {linkLabel && path ? (
          <Link
            to={path}
            onClick={onOpen}
            data-testid="glocke-link"
            className="mt-1 inline-flex min-h-11 items-center text-row font-semibold text-brand hover:underline"
          >
            {linkLabel}
          </Link>
        ) : null}
      </span>
      {unread ? (
        <span
          data-testid="glocke-ungelesen"
          aria-hidden
          className="mt-space-2 size-2 flex-none rounded-full bg-brand-orange"
        />
      ) : null}
    </>
  );
  return (
    <li className="border-b border-line last:border-b-0">
      {path && !linkLabel ? (
        <Link
          to={path}
          hash={ownPlace ? undefined : `eintrag-${item.id}`}
          onClick={onOpen}
          className="flex gap-3 rounded-tile px-2 py-3 hover:bg-tile"
        >
          {content}
        </Link>
      ) : (
        <div className="flex gap-3 px-2 py-3">{content}</div>
      )}
    </li>
  );
}

/** Eine Neuerung im Abschnitt „Neu im ERP“ (Story 1.99): „Neu: …“, grau Story und Datum, Klick führt an die Stelle. */
function NewsEntry({
  note,
  onOpen,
}: {
  note: ReleaseNote;
  onOpen: () => void;
}) {
  return (
    <li className="border-b border-line last:border-b-0">
      <Link
        {...releaseNoteLink(note.href)}
        onClick={onOpen}
        className="flex gap-3 rounded-tile px-2 py-3 hover:bg-tile"
      >
        <span
          aria-hidden
          className="grid size-icon-circle flex-none place-items-center rounded-full bg-fill text-ink-2"
        >
          <Megaphone size={16} strokeWidth={1.5} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-row font-semibold text-ink [overflow-wrap:anywhere]">
            {RELEASE_NOTE_TEXTS.text(note.text)}
          </span>
          <span className="type-meta block text-ink-3">
            {RELEASE_NOTE_TEXTS.story(note.key)} · {formatDate(note.date)}
          </span>
        </span>
      </Link>
    </li>
  );
}

/** Abschnitt „Neu im ERP“ unter den Benachrichtigungen (Story 1.99) mit Link zur Seite. */
function NewsSection({
  news,
  onOpen,
}: {
  news: ReleaseNote[];
  onOpen: () => void;
}) {
  if (news.length === 0) return null;
  return (
    <section
      aria-labelledby="glocke-neu-im-erp"
      className="mt-3 border-t border-line pt-3"
    >
      <h3 id="glocke-neu-im-erp" className="type-label px-2 text-ink-2">
        {RELEASE_NOTE_TEXTS.title}
      </h3>
      <ul
        aria-label={RELEASE_NOTE_TEXTS.title}
        className="mt-1 flex list-none flex-col p-0 pointer-coarse:gap-2"
      >
        {news.map((note) => (
          <NewsEntry key={note.id} note={note} onOpen={onOpen} />
        ))}
      </ul>
      <Link
        to={RELEASE_NOTES_PATH}
        onClick={onOpen}
        className="mt-1 inline-flex min-h-11 items-center px-2 text-row font-semibold text-brand hover:underline"
      >
        {RELEASE_NOTE_TEXTS.all}
      </Link>
    </section>
  );
}

/**
 * Tag-Gruppe eines Zeitpunkts (Europe/Berlin) gegen das fachliche Jetzt (Story 22.26): mit gestellter Testuhr landet eine
 * Meldung von heute (Testuhr) unter „Heute“; ohne Testuhr ist das die Uhr des Browsers.
 */
function dayGroup(at: string, now: Date): string {
  const day = isoDateInBerlin(new Date(at));
  if (day === isoDateInBerlin(now)) return BELL_TEXTS.today;
  if (day === previousIsoDateInBerlin(now)) return BELL_TEXTS.yesterday;
  return BELL_TEXTS.older;
}

/** Einträge einer Auswahl, gruppiert nach Heute/Gestern/Älter (Entwurf A05); eine Liste „Benachrichtigungen“. */
function NotificationGroups({
  items,
  unread,
  onOpen,
}: {
  items: NotificationItem[];
  unread: ReadonlySet<string>;
  onOpen: () => void;
}) {
  const { now } = useBusinessNow();
  let last: string | null = null;
  const rows: ReactNode[] = [];
  for (const item of items) {
    const group = dayGroup(item.at, now);
    if (group !== last) {
      last = group;
      rows.push(
        <li
          key={`gruppe-${group}`}
          role="none"
          className="pt-space-3 first:pt-0"
        >
          <h3 className="type-label px-space-2 text-ink-3">{group}</h3>
        </li>,
      );
    }
    rows.push(
      <NotificationEntry
        key={item.id}
        item={item}
        unread={unread.has(item.id)}
        onOpen={onOpen}
      />,
    );
  }
  return (
    <ol
      aria-label={BELL_TEXTS.title}
      className="mt-space-2 flex list-none flex-col p-0 pointer-coarse:gap-2"
    >
      {rows}
    </ol>
  );
}

/** Ausgegrautes Entwurfsteil ohne Funktion (Rudolf 17.10.2026). */
function Later({
  children,
  className,
  hint = BELL_TEXTS.later,
}: {
  children: ReactNode;
  className?: string;
  hint?: string;
}) {
  return (
    <span
      aria-disabled="true"
      title={hint}
      className={cn(
        "inline-flex min-h-11 items-center gap-space-2 text-row text-ink-3",
        className,
      )}
    >
      {children}
      <span className="type-meta">({hint})</span>
    </span>
  );
}

/**
 * Reiter „Ereignisse“ (Ticket 1.243, früher Karte „Letzte Ereignisse“ der Startseite, Story 1.79): Zeitleiste der letzten
 * Ereignisse im Team, neueste zuerst, Uhrzeit links, Punkt und feine Linie. Nur lesen; Klick führt zum Datensatz.
 */
function EventsTimeline({
  activity,
  onOpen,
}: {
  activity: ReturnType<typeof useActivity>;
  onOpen: () => void;
}) {
  const items = activity.data?.items ?? [];
  return (
    <>
      {activity.isPending ? (
        <p className="type-meta mt-space-3 px-space-2 text-ink-3">Lädt …</p>
      ) : null}
      {activity.error ? (
        <p className="type-meta mt-space-3 px-space-2 text-clarify">
          {activity.error instanceof ApiProblem
            ? activity.error.detail
            : "Etwas ist schiefgelaufen."}
        </p>
      ) : null}
      {activity.data && items.length === 0 ? (
        <p className="mt-space-3 px-space-2 text-row text-ink-3">
          {BELL_TEXTS.eventsEmpty}
        </p>
      ) : null}
      {items.length > 0 ? (
        <ol
          aria-label={BELL_TEXTS.eventsLong}
          className="mt-space-3 flex list-none flex-col p-0"
        >
          {items.map((item, i) => {
            const path = entityPath(item.entityType, item.entityId);
            const content = (
              <>
                <span className="type-meta block text-ink-3 tabular-nums">
                  {formatWhen(item.at)}
                </span>
                <span className="block text-row text-ink [overflow-wrap:anywhere]">
                  {item.text}
                </span>
              </>
            );
            return (
              <li key={item.id} className="relative pb-space-4 pl-6 last:pb-0">
                {/* Punkt und feine Linie der Zeitleiste (nur Schmuck). */}
                <span
                  aria-hidden
                  className="absolute top-2 left-1 size-2 rounded-full bg-brand"
                />
                {i < items.length - 1 ? (
                  <span
                    aria-hidden
                    className="absolute top-4 bottom-0 left-timeline-x w-px bg-line"
                  />
                ) : null}
                {path ? (
                  <Link
                    to={path}
                    onClick={onOpen}
                    className="block rounded-tile hover:underline pointer-coarse:min-h-11"
                  >
                    {content}
                  </Link>
                ) : (
                  <div>{content}</div>
                )}
              </li>
            );
          })}
        </ol>
      ) : null}
      <Later className="mt-space-2 px-space-2">{BELL_TEXTS.protocol}</Later>
    </>
  );
}

function useActivity(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ACTIVITY_KEY,
    queryFn: () =>
      apiFetch<{ items: ActivityItem[] }>(
        getToken,
        `/api/v1/activity?limit=${ACTIVITY_LIMIT}`,
      ),
    enabled,
  });
}

/**
 * Seitenpanel der Glocke (Ticket 1.243, Entwurf A05): rechts über abgedunkelter Seite (am Handy Vollbild mit „Zurück“,
 * EXPERIENCE §10.2). Kopf mit Titel, Reiter-Band Alle/Ungelesen/Erwähnungen/Ereignisse (`role=tablist`, Pfeil links/rechts
 * wechselt), darunter die Auswahl; Fuß mit Zählzeile. Fokus beim Öffnen auf die Überschrift, bleibt im Panel; Esc,
 * Schließen oder Klick daneben schließt, der Fokus kehrt auf die Glocke zurück.
 */
function NotificationsPanel({
  phone,
  opener,
  onClose,
  notifications,
  unreadIds,
  onMarkAll,
  headingId,
  panelId,
}: {
  phone: boolean;
  opener: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  notifications: ReturnType<typeof useNotifications>;
  unreadIds: ReadonlySet<string>;
  onMarkAll: () => void;
  headingId: string;
  panelId: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [tab, setTab] = useState<BellTab>("all");
  const tabsId = useId();
  useModalFocus(panel, opener, onClose, () => heading.current);
  const staffOnly = useSession().me.role === "mitarbeiter";
  const tabOrder = staffOnly ? STAFF_TAB_ORDER : TAB_ORDER;
  const activity = useActivity(!staffOnly);

  const items = notifications.data?.items ?? [];
  const unreadItems = items.filter((i) => unreadIds.has(i.id));
  const mentions = items.filter((i) => i.kind === "mention");
  const events = activity.data?.items.length ?? null;
  const counts: Record<BellTab, number | null> = {
    all: items.length,
    unread: unreadItems.length,
    mentions: mentions.length,
    events,
  };
  const labels: Record<BellTab, string> = {
    all: BELL_TEXTS.all,
    unread: BELL_TEXTS.unread,
    mentions: BELL_TEXTS.mentions,
    events: BELL_TEXTS.events,
  };

  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next =
      (tabOrder.indexOf(tab) + step + tabOrder.length) % tabOrder.length;
    setTab(tabOrder[next]!);
    tabRefs.current[next]?.focus();
  };

  const loading = notifications.isPending ? (
    <p className="type-meta mt-space-3 px-space-2 text-ink-3">Lädt …</p>
  ) : null;
  const failed = notifications.error ? (
    <p className="type-meta mt-space-3 px-space-2 text-clarify">
      {notifications.error instanceof ApiProblem
        ? notifications.error.detail
        : "Etwas ist schiefgelaufen."}
    </p>
  ) : null;
  const empty = (text: string) => (
    <p className="mt-space-3 px-space-2 text-row text-ink-3">{text}</p>
  );
  const shown =
    tab === "unread" ? unreadItems : tab === "mentions" ? mentions : items;
  const emptyText =
    tab === "unread"
      ? BELL_TEXTS.unreadEmpty
      : tab === "mentions"
        ? BELL_TEXTS.mentionsEmpty
        : COLLAB_TEXTS.notificationsEmpty;

  // Im Portal: die Kopfzeile (Glas mit Unschärfe) wäre sonst der Bezugsrahmen von `fixed`.
  return createPortal(
    <>
      {phone ? null : (
        <div
          aria-hidden
          data-testid="glocke-abdunklung"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-scrim"
        />
      )}
      <div
        ref={panel}
        id={panelId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="glocke-panel"
        className={cn(
          "fixed z-50 flex flex-col overflow-x-hidden bg-card",
          phone
            ? "inset-0"
            : "right-space-4 bottom-space-4 left-space-4 rounded-card shadow-panel tablet:left-auto tablet:w-panel-w",
        )}
        style={
          phone
            ? undefined
            : { top: "calc(var(--banner-h, 0px) + var(--space-4))" }
        }
      >
        <div className="flex flex-none items-center gap-space-3 px-space-4 pt-space-4 pb-space-3">
          {phone ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 flex-none cursor-pointer items-center rounded-full bg-secondary px-4 text-row text-secondary-foreground"
            >
              {BELL_TEXTS.back}
            </button>
          ) : null}
          <h2
            id={headingId}
            ref={heading}
            tabIndex={-1}
            className="type-card-title min-w-0 flex-1"
          >
            {BELL_TEXTS.title}
          </h2>
          {phone ? null : (
            <button
              type="button"
              onClick={onClose}
              aria-label={BELL_TEXTS.close}
              title={BELL_TEXTS.close}
              className="grid size-icon-circle flex-none cursor-pointer place-items-center rounded-full bg-fill text-ink-2 hover:text-ink"
            >
              <X size={18} strokeWidth={1.75} aria-hidden />
            </button>
          )}
        </div>
        <div className="flex flex-none flex-wrap items-center justify-between gap-space-2 px-space-4">
          <div
            role="tablist"
            aria-label={BELL_TEXTS.tabs}
            onKeyDown={onTabKey}
            className="flex max-w-full flex-wrap gap-space-1"
          >
            {tabOrder.map((key, i) => {
              const active = key === tab;
              const count = counts[key];
              return (
                <button
                  key={key}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`${tabsId}-${key}`}
                  aria-selected={active}
                  aria-controls={`${tabsId}-inhalt`}
                  tabIndex={active ? 0 : -1}
                  title={key === "events" ? BELL_TEXTS.eventsLong : undefined}
                  onClick={() => setTab(key)}
                  className={cn(
                    "inline-flex h-9 cursor-pointer items-center gap-space-2 rounded-full px-space-3 text-row whitespace-nowrap pointer-coarse:h-11",
                    active
                      ? "bg-brand text-on-brand"
                      : "bg-fill text-ink-2 hover:text-ink",
                  )}
                >
                  {key === "events" ? (
                    <span className="sr-only">Letzte </span>
                  ) : null}
                  {labels[key]}
                  {count !== null ? (
                    <span
                      className={cn(
                        "tabular-nums",
                        active ? "text-on-brand-count" : "text-ink-3",
                      )}
                    >
                      {count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          {tab === "events" ? null : (
            <button
              type="button"
              onClick={onMarkAll}
              disabled={unreadItems.length === 0}
              className="inline-flex min-h-11 cursor-pointer items-center text-row font-semibold text-link hover:underline disabled:cursor-default disabled:text-ink-3 disabled:no-underline"
            >
              {BELL_TEXTS.markAll}
            </button>
          )}
        </div>
        <div
          role="tabpanel"
          id={`${tabsId}-inhalt`}
          aria-labelledby={`${tabsId}-${tab}`}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-space-4 pb-space-4"
        >
          {tab === "events" ? (
            <EventsTimeline activity={activity} onOpen={onClose} />
          ) : (
            <>
              {loading}
              {failed}
              {notifications.data && shown.length === 0
                ? empty(emptyText)
                : null}
              {shown.length > 0 ? (
                <NotificationGroups
                  items={shown}
                  unread={unreadIds}
                  onOpen={onClose}
                />
              ) : null}
              {tab === "all" ? (
                <NewsSection
                  news={notifications.data?.news ?? []}
                  onOpen={onClose}
                />
              ) : null}
            </>
          )}
        </div>
        <div className="flex flex-none flex-wrap items-center justify-between gap-space-2 border-t border-line px-space-4 py-space-2">
          <span
            data-testid="glocke-zaehlzeile"
            className="type-meta text-ink-3"
          >
            {BELL_TEXTS.count(items.length, unreadItems.length)}
          </span>
          <Later hint={BELL_TEXTS.laterSettings}>{BELL_TEXTS.settings}</Later>
        </div>
      </div>
    </>,
    document.body,
  );
}

/**
 * Glocke im Kopf (EXPERIENCE §2.4, §4.1, §6.5; DESIGN „Symbolknopf“): runder 44-px-Knopf mit Warnpunkt bei Neuem. Klick
 * öffnet das Seitenpanel (Ticket 1.243, Entwurf A05; am Handy Vollbild mit „Zurück“, §10.2) mit @Erwähnungen, Änderungen an
 * gefolgten Datensätzen, für Admins Systemhinweise, „Neu im ERP“ und im Reiter „Ereignisse“ den letzten Ereignissen im Team –
 * zum Wissen, nicht zum Erledigen. Beim Öffnen gilt alles Gezeigte als gesehen; „Ungelesen“ zeigt den Stand beim Öffnen.
 * Keine Einblendungen, keine E-Mail.
 */
export function NotificationBell() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [open, setOpen] = useState(false);
  const phone = useScreen() === "phone";
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const headingId = useId();
  const seen = useMutation({
    mutationFn: (upTo: string) =>
      apiFetch<NotificationList>(getToken, "/api/v1/notifications/seen", {
        method: "POST",
        body: { upTo },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(NOTIFICATIONS_KEY, data);
      // Seite „Neu im ERP“ (falls offen): „Neu“ ohne Neuladen entfernen (Story 1.113).
      void queryClient.invalidateQueries({ queryKey: RELEASE_NOTES_KEY });
    },
  });
  const items = notifications.data?.items ?? [];
  const unseen = notifications.data?.unseen ?? false;

  // „Ungelesen“ (Ticket 1.243): Stand beim Öffnen festhalten, sonst leert das Gesehen-Melden den Reiter sofort. Was
  // während des Öffnens neu ankommt, kommt dazu; „Alle gelesen“ leert den festgehaltenen Stand.
  const [openedUnread, setOpenedUnread] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const unreadNow = items.filter((i) => i.unread).map((i) => i.id);
  const unreadIds: ReadonlySet<string> = new Set([
    ...openedUnread,
    ...unreadNow,
  ]);
  const toggle = () => {
    if (!open) setOpenedUnread(new Set(unreadNow));
    setOpen(!open);
  };

  // Geöffnet und Neues da: alles bis zum neuesten gezeigten Eintrag gilt als gesehen.
  // Je Stand nur ein Versuch (schlägt er fehl, bleibt der Warnpunkt bis zum nächsten Öffnen).
  // „Neu im ERP“ (Story 1.99) zählt mit: der neueste Eintrag aus beiden Teilen.
  const newsNewest = notifications.data?.news?.[0]?.id;
  const newest = [items[0]?.id, newsNewest]
    .filter((id): id is string => id !== undefined)
    .sort()
    .at(-1);
  const attempted = useRef<string | null>(null);
  const { mutate: markSeen } = seen;
  useEffect(() => {
    if (!open) {
      attempted.current = null;
      return;
    }
    if (unseen && newest && attempted.current !== newest) {
      attempted.current = newest;
      markSeen(newest);
    }
  }, [open, unseen, newest, markSeen]);

  const close = () => setOpen(false);
  const markAll = () => {
    setOpenedUnread(new Set());
    if (unseen && newest) markSeen(newest);
  };

  return (
    <div className="relative">
      <button
        ref={button}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={
          unseen ? "Benachrichtigungen, Neues vorhanden" : "Benachrichtigungen"
        }
        title="Benachrichtigungen"
        // Design S3: runder Chip 44 px mit feinem Rand, Symbol dunkelblau; Punkt in TILL-Orange (eine der drei winzigen Stellen).
        className={cn(
          "relative grid flex-none cursor-pointer place-items-center rounded-full text-ink-2 hover:text-ink",
          phone ? "size-11" : "size-10 transition-colors duration-150 hover:bg-hover pointer-coarse:size-11",
        )}
      >
        <Bell size={18} strokeWidth={1.5} aria-hidden />
        {unseen ? (
          <span
            data-testid="glocke-warnpunkt"
            aria-hidden
            className="absolute top-dot-top right-space-3 size-2 rounded-full bg-brand-orange ring-2 ring-card"
          />
        ) : null}
      </button>
      {open ? (
        <NotificationsPanel
          phone={phone}
          opener={button}
          onClose={close}
          notifications={notifications}
          unreadIds={unreadIds}
          onMarkAll={markAll}
          headingId={headingId}
          panelId={panelId}
        />
      ) : null}
    </div>
  );
}
